import React from 'react';
import { Shield, Cookie, Settings, Eye, Lock, Database } from 'lucide-react';

const CookiePolicy: React.FC = () => {
  const cookieTypes = [
    {
      name: "Essential Cookies",
      description: "These cookies are necessary for the website to function properly. They enable basic functions like page navigation and access to secure areas of the website.",
      examples: ["Authentication cookies", "Session management", "Security cookies"],
      necessary: true
    },
    {
      name: "Analytics Cookies",
      description: "These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.",
      examples: ["Google Analytics", "Page view tracking", "User behavior analysis"],
      necessary: false
    },
    {
      name: "Functional Cookies",
      description: "These cookies enable enhanced functionality and personalization, such as remembering your preferences and settings.",
      examples: ["Language preferences", "Theme settings", "Form data retention"],
      necessary: false
    },
    {
      name: "Marketing Cookies",
      description: "These cookies are used to track visitors across websites to display relevant and engaging advertisements.",
      examples: ["Social media pixels", "Advertising networks", "Retargeting"],
      necessary: false
    }
  ];

  const cookieDetails = [
    {
      name: "_ga",
      purpose: "Google Analytics - Used to distinguish unique users",
      duration: "2 years",
      provider: "Google"
    },
    {
      name: "_gid",
      purpose: "Google Analytics - Used to distinguish users",
      duration: "24 hours",
      provider: "Google"
    },
    {
      name: "session_id",
      purpose: "Maintains your login session",
      duration: "Session",
      provider: "StartupStreak"
    },
    {
      name: "preferences",
      purpose: "Stores your site preferences and settings",
      duration: "1 year",
      provider: "StartupStreak"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-32 pb-40 bg-gradient-to-br from-startup-navy via-startup-teal to-startup-peach text-white animate-fade-in">
        <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:50px_50px]" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="flex justify-center mb-8">
              <div className="w-20 h-20 border border-white/20 rounded-full flex items-center justify-center">
                <Cookie className="w-10 h-10 text-white" />
              </div>
            </div>
            
            <h1 className="text-6xl md:text-7xl font-light tracking-tight mb-8 leading-tight">
              Cookie Policy
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-12 leading-relaxed max-w-2xl mx-auto font-light">
              Learn how we use cookies and similar technologies to enhance your experience on StartupStreak
            </p>
            
            <div className="flex items-center justify-center gap-2 text-black-400">
              <Shield className="w-5 h-5" />
              <span>Last updated: August 15, 2025</span>
            </div>
          </div>
        </div>
      </section>

      {/* What Are Cookies */}
      <section className="py-32 bg-white text-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-light mb-8 text-center">What Are Cookies?</h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed text-center">
              Cookies are small text files that are stored on your device when you visit our website. 
              They help us provide you with a better experience and understand how you use our platform.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8 mt-16">
              <div className="text-center">
                <div className="w-16 h-16 border border-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Database className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-medium mb-4">Data Storage</h3>
                <p className="text-gray-600 leading-relaxed">
                  Small pieces of information stored on your device to remember your preferences
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 border border-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Settings className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-medium mb-4">Personalization</h3>
                <p className="text-gray-600 leading-relaxed">
                  Help us customize your experience based on your previous interactions
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 border border-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Eye className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-medium mb-4">Analytics</h3>
                <p className="text-gray-600 leading-relaxed">
                  Provide insights into how our website is used to improve our services
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Types of Cookies */}
      <section className="py-32 bg-gray-50 text-black">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-light mb-8 text-center">Types of Cookies We Use</h2>
            <p className="text-xl text-gray-600 mb-16 leading-relaxed text-center max-w-3xl mx-auto">
              We use different types of cookies for various purposes. Here's a breakdown of what each type does:
            </p>
            
            <div className="grid lg:grid-cols-2 gap-8">
              {cookieTypes.map((type, index) => (
                <div key={index} className="bg-white p-8 rounded-lg border border-gray-200 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-2xl font-medium text-black">{type.name}</h3>
                    {type.necessary && (
                      <span className="bg-green-100 text-green-800 text-xs px-3 py-1 rounded-full font-medium">
                        Necessary
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {type.description}
                  </p>
                  <div>
                    <h4 className="font-medium text-black mb-3">Examples:</h4>
                    <ul className="space-y-2">
                      {type.examples.map((example, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-gray-600">
                          <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                          {example}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Specific Cookies */}
      <section className="py-32 bg-white text-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-light mb-8 text-center">Specific Cookies We Use</h2>
            <p className="text-xl text-gray-600 mb-16 leading-relaxed text-center">
              Here are the specific cookies we use and what they do:
            </p>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-4 px-6 font-medium text-black">Cookie Name</th>
                    <th className="text-left py-4 px-6 font-medium text-black">Purpose</th>
                    <th className="text-left py-4 px-6 font-medium text-black">Duration</th>
                    <th className="text-left py-4 px-6 font-medium text-black">Provider</th>
                  </tr>
                </thead>
                <tbody>
                  {cookieDetails.map((cookie, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-6 font-mono text-sm text-blue-600">{cookie.name}</td>
                      <td className="py-4 px-6 text-gray-700">{cookie.purpose}</td>
                      <td className="py-4 px-6 text-gray-600">{cookie.duration}</td>
                      <td className="py-4 px-6 text-gray-600">{cookie.provider}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Third-Party Cookies */}
      <section className="py-32 bg-gray-50 text-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-light mb-8 text-center">Third-Party Cookies</h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed text-center">
              Some cookies are placed by third-party services that appear on our pages:
            </p>
            
            <div className="bg-white p-8 rounded-lg border border-gray-200">
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-medium text-black mb-3">Google Analytics</h3>
                  <p className="text-gray-600 leading-relaxed">
                    We use Google Analytics to understand how visitors use our website. This helps us improve our services 
                    and provide better user experiences. Google Analytics cookies collect information anonymously.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-medium text-black mb-3">Social Media Platforms</h3>
                  <p className="text-gray-600 leading-relaxed">
                    When you interact with social media features on our site (like sharing buttons), 
                    those platforms may set cookies to track your activity.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-medium text-black mb-3">Payment Processors</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Our payment partners may use cookies to ensure secure transactions and remember your payment preferences.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Managing Cookies */}
      <section className="py-32 bg-white text-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-light mb-8 text-center">Managing Your Cookie Preferences</h2>
            <p className="text-xl text-gray-600 mb-16 leading-relaxed text-center">
              You have control over how cookies are used on our website:
            </p>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 border border-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Settings className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-medium mb-4">Browser Settings</h3>
                <p className="text-gray-600 leading-relaxed">
                  Most browsers allow you to control cookies through their settings. You can delete existing cookies 
                  and choose whether to accept new ones.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 border border-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Lock className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-medium mb-4">Cookie Consent</h3>
                <p className="text-gray-600 leading-relaxed">
                  When you first visit our site, you'll see a cookie banner where you can choose which types 
                  of cookies to accept or reject.
                </p>
              </div>
            </div>
            
            <div className="mt-12 text-center">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-2xl mx-auto">
                <h3 className="text-lg font-medium text-blue-900 mb-3">Important Note</h3>
                <p className="text-blue-800 leading-relaxed">
                  Some cookies are essential for our website to function properly. If you disable these cookies, 
                  certain features may not work as expected.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Updates and Contact */}
      <section className="py-32 bg-gradient-to-br from-startup-navy via-startup-teal to-startup-peach text-white animate-fade-in">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-light mb-8">
              Questions About Cookies?
            </h2>
            <p className="text-xl md:text-2xl mb-12 text-gray-300 font-light">
              If you have any questions about our cookie policy or how we use cookies, 
              please don't hesitate to contact us.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a href="mailto:hello@startupstreak.com" className="inline-block">
                <button className="bg-white text-startup-navy hover:bg-startup-gold hover:text-startup-navy text-lg px-12 py-4 font-medium transition-all hover:ring-2 hover:ring-startup-gold/60">
                  Contact Privacy Team
                </button>
              </a>
              <a href="/privacy" className="inline-block">
                <button className="bg-white border border-startup-teal text-black hover:bg-white hover:text-startup-navy hover:border-white text-lg px-12 py-4 font-medium transition-all">
                  View Privacy Policy
                </button>
              </a>
            </div>
            
            <div className="mt-16 pt-8 border-t border-gray-800">
              <p className="text-black-400 text-sm">
                This cookie policy was last updated on August 15, 2025. 
                We may update this policy from time to time to reflect changes in our practices or for other operational, 
                legal, or regulatory reasons.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CookiePolicy;
